/**
 * @file has-feature.js
 * @description Directiva Vue `v-has-feature` análoga a `v-has-permission`,
 *              pero con semántica de feature flags por tenant.
 *
 *              SEMÁNTICA DEL DEFAULT — feature ausente en
 *              `Organization.features` ⇒ ENCENDIDA. Para apagar, el
 *              platform-admin guarda `false` explícito desde el tablero.
 *
 *              Lee las features del JWT/store del usuario actual:
 *                `store.state.user.organization.features`
 *
 *              Si la feature está APAGADA, remueve el elemento del DOM (el
 *              mismo patrón que v-if).
 *
 * @example
 *   button(v-has-feature="'ooss.validator'") Validar afiliado
 *
 *              IMPORTANTE: esta directiva NO se aplica todavía a ningún item
 *              de UI existente. Es infra para fases siguientes. Cuando
 *              quieras esconder un menú/sección de Vilella, agregale
 *              `v-has-feature="'<clave>'"` y prendé/apagá desde el tablero.
 */

import store from "@/store";

/**
 * @description Resuelve si una feature está encendida en el tenant actual,
 *              con fallback default-ON cuando la clave no está seteada.
 * @param {string} key
 * @returns {boolean}
 */
function isEnabled(key) {
  const features = store.state.user?.organization?.features;
  if (!features) return true;
  const value = typeof features === "object" ? features[key] : undefined;
  if (value === undefined || value === null) return true;
  return Boolean(value);
}

const hasFeature = {
  bind(el, binding) {
    const key = binding.value;
    if (!key) return;
    if (!isEnabled(key)) {
      el.style.display = "none";
      el.setAttribute("data-feature-off", key);
    }
  },
  update(el, binding) {
    const key = binding.value;
    if (!key) return;
    if (!isEnabled(key)) {
      el.style.display = "none";
      el.setAttribute("data-feature-off", key);
    } else {
      el.style.display = "";
      el.removeAttribute("data-feature-off");
    }
  },
};

export default hasFeature;
